import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { useIwEditingStore, useIwExecutionStore } from '@/entities/interwills';
import { getBeneficiaryFullName } from '@/entities/interwills/helpers';
import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { dates } from '@/shared/utils/dates';

import { RecordingVideo } from '../index';

import { ClosableAlert, VideoRecorder } from './index';

type VideoTabProps = {
  roleName: string;
  witnessId?: string;
  setVideo: (obj: RecordingVideo) => void;
};

export const VideoTab: FC<VideoTabProps> = ({ roleName, witnessId, setVideo }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'recordingModal' });

  const [showWarning, setShowWarning] = useState(true);

  const editing = useIwEditingStore();
  const { willNumber, createdDate, pdfPageCount } = useIwExecutionStore();

  const witness = useMemo(() => {
    return editing.witnesses.find((item) => item.id === witnessId);
  }, [witnessId]);

  const promptCommon = {
    dateOfBirth:
      editing.testator.birthDate &&
      dates.formatMask(new Date(editing.testator.birthDate), {
        month: 'long',
      }),
    willNumber,
    date:
      createdDate &&
      dates.formatMask(new Date(createdDate), {
        month: 'long',
      }),
    pageCount: pdfPageCount,
    witnessCount: editing.witnesses.length,
  };

  const testatorName = getBeneficiaryFullName({
    firstName: editing.testator.firstName ?? '',
    lastName: editing.testator.lastName ?? '',
  });

  return (
    <>
      <VideoRecorder setVideo={setVideo} />
      <PromptWrapper>
        {showWarning && (
          <ClosableAlertStyled onClose={() => setShowWarning(false)}>
            {t('lengthWarning')}
          </ClosableAlertStyled>
        )}
        <Prompt>
          <PromptTitle>{t('promptTitle')}</PromptTitle>
          <RoleTitle>{t('attestation', { role: roleName })}</RoleTitle>
          {witness ? (
            <div>
              <div>
                {t('witnessPrompt', {
                  fullname: getBeneficiaryFullName({
                    firstName: witness.firstName ?? '',
                    lastName: witness.lastName ?? '',
                  }),
                  ...promptCommon,
                })}
              </div>
              <div>
                {t('witnessPrompt2', {
                  testatorName,
                })}
              </div>
              <List>
                <li>{t('witnessList1')}</li>
                <li>{t('witnessList2')}</li>
                <li>{t('witnessList3')}</li>
                <li>{t('witnessList4')}</li>
                <li>{t('witnessList5')}</li>
              </List>
            </div>
          ) : (
            <div>
              {t('testatorPrompt', {
                fullname: testatorName,
                ...promptCommon,
              })}
            </div>
          )}
        </Prompt>
      </PromptWrapper>
    </>
  );
};

const PromptWrapper = styled.div`
  max-width: 380px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;

  ${media.iwMobileOrLandscape} {
    position: absolute;
    inset: 0;
  }
  ${media.iwMobileLandscape} {
    margin-top: 88px;
    max-width: unset;
    left: 16px;
  }
`;

const Prompt = styled.div`
  padding: 12px;
  background: ${COLORS.systemLight50};
  border-radius: 8px;
  color: ${COLORS.white};
  overflow: auto;
  max-height: 100%;

  ${media.iwMobile} {
    position: absolute;
    left: 16px;
    width: calc(100vw - 32px);
    bottom: 112px;
    max-height: calc(100% - 360px);
  }

  ${media.iwMobileLandscape} {
    max-width: calc(50% - 58px);
  }
`;

const PromptTitle = styled.div`
  font-weight: 700;
  margin-bottom: 16px;
`;

const RoleTitle = styled.div`
  font-weight: 700;
  margin-bottom: 8px;
`;

const List = styled.ul`
  padding-left: 16px;
`;

const ClosableAlertStyled = styled(ClosableAlert)`
  ${media.iwMobile} {
    position: absolute;
    left: 16px;
    width: calc(100vw - 32px);
    top: 148px;
  }

  ${media.iwMobileLandscape} {
    position: absolute;
    right: 16px;
    width: 50%;
    top: 148px;
    max-width: 330px;
    top: 0;
  }
`;
